// src/components/SectionIcon.js
import React from 'react';
import { useIconSettings } from '../context/IconSettingsContext';

// The shared action icons (Settings > Iconos y Colores > Acciones) get a
// hover color that tints the whole button/link they sit inside — unlike
// nav/hub items, an action icon has no single tile/link of its own to
// color, so instead each icon just carries an `action-icon-<key>` class,
// and SectionColorProvider renders a global <style> with `:has()` rules
// (built from the same colors this hook would read) that reach up from the
// icon to its containing button/link on hover. Scoped to just these keys
// so nav/hub tiles keep their existing, unrelated hover-color behavior
// untouched.
export const ACTION_ICON_KEYS = new Set([
  'action_view', 'action_refresh', 'action_add', 'action_edit', 'action_delete',
  'action_download', 'action_upload', 'action_clear',
  'action_export_excel', 'action_export_pdf',
  // Page-specific action icons (Settings > Iconos y Colores > Acciones específicas de página)
  'action_bring_forward', 'action_saved_history', 'action_lock_locked', 'action_lock_unlocked',
  'action_import', 'action_search', 'action_ai', 'action_filters', 'action_export_html',
  'action_permissions', 'action_send_email', 'action_send_whatsapp', 'returns_overseas_toggle', 'returns_slack_toggle', 'action_duplicate',
  'action_reset_password', 'action_deactivate', 'action_activate',
  // View/tab toggles (Settings > Iconos y Colores > Vistas / Pestañas)
  'billing_view_form', 'billing_view_split', 'billing_view_preview',
  'tracking_view_list', 'tracking_view_split', 'tracking_view_map',
  'analytics_tab_cartera', 'analytics_tab_cajas_cliente', 'analytics_tab_spot_contrato', 'analytics_tab_cajas_semana',
]);

// Renders a section's icon: custom uploaded image(s) if set for `iconKey`,
// otherwise the hardcoded `defaultEmoji`. Drop-in replacement for a bare
// emoji span/div wherever a navbar item or hub tile shows its icon.
//
// A key can have a light and/or dark variant. If both are set, both <img>s
// are rendered and CSS ([data-theme]) shows only the one matching the
// current theme — the same technique already used for the sidebar logo
// (see .sb-logo-img.sb-light/.sb-dark in Navbar.css). If only one variant
// is set, it's used for both themes rather than leaving the icon blank
// in the other.
export default function SectionIcon({ iconKey, defaultEmoji, size, className, style }) {
  const { icons } = useIconSettings();
  const entry = iconKey ? icons[iconKey] : null;
  const light = entry?.light;
  const dark = entry?.dark;

  const isActionIcon = iconKey && ACTION_ICON_KEYS.has(iconKey);
  const hoverClass = isActionIcon ? `action-icon action-icon-${iconKey}` : '';

  if (!light && !dark) {
    return (
      <span className={`${hoverClass} ${className || ''}`.trim()} style={style}>
        {defaultEmoji}
      </span>
    );
  }

  const sizeStyle = {
    width: size || '1em',
    height: size || '1em',
    objectFit: 'contain',
    verticalAlign: 'middle',
    ...style,
  };

  if (light && dark) {
    // No `display` here — the section-icon-light/dark classes control
    // show/hide by theme via CSS, and an inline display would out-rank them.
    return (
      <>
        <img src={light} alt="" className={`${hoverClass} ${className || ''} section-icon-light`.trim()} style={sizeStyle} />
        <img src={dark} alt="" className={`${hoverClass} ${className || ''} section-icon-dark`.trim()} style={sizeStyle} />
      </>
    );
  }

  return <img src={light || dark} alt="" className={`${hoverClass} ${className || ''}`.trim()} style={{ display: 'inline-block', ...sizeStyle }} />;
}
